import { Component, Inject, OnInit, AfterViewInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'web-account-management-dialog',
  templateUrl: './account-management-dialog.component.html',
  styleUrls: ['./account-management-dialog.component.scss']
})
export class AccountManagementDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  stripeConnectInstance: any;
  accountManagementComponent: any = null;
  onboardingComponent: any = null;
  bundleItem: string | undefined;
  title: string = 'Account Management';
  isOnboarding: boolean = false;

  @ViewChild("accountManagementContainer", { static: false }) accountManagementContainer!: ElementRef<HTMLDivElement>;

  stripeComponentName: string = 'account-management';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { 
      stripeConnectInstance: any; 
      bundleItem?: string; 
      title?: string;
      onboardingComponent?: any;
      isOnboarding?: boolean;
      /** Override the Stripe component to mount (default: 'account-management') */
      stripeComponentName?: string;
    },
    private dlg: MatDialogRef<AccountManagementDialogComponent>,
  ) {
    this.stripeConnectInstance = data.stripeConnectInstance;
    this.bundleItem = data.bundleItem;
    this.title = data.title || 'Account Management';
    this.onboardingComponent = data.onboardingComponent;
    this.isOnboarding = data.isOnboarding || !!data.onboardingComponent;
    this.stripeComponentName = data.stripeComponentName || 'account-management';
  }

  ngOnInit() {
    // Component initialization
  }

  ngAfterViewInit() {
    // Wait for container to be available, then mount component
    setTimeout(() => {
      if (this.isOnboarding) {
        if (this.onboardingComponent) {
          // Use pre-created onboarding component
          this.mountOnboardingComponent();
        } else {
          // Create onboarding component
          this.createAndMountOnboardingComponent();
        }
      } else {
      this.mountAccountManagementComponent();
      }
    }, 100);
  }

  ngOnDestroy() {
    this.cleanup();
  }

  private mountAccountManagementComponent() {
    if (!this.stripeConnectInstance) {
      console.error("Cannot mount account management component: missing Stripe Connect instance");
      return;
    }

    if (!this.accountManagementContainer?.nativeElement) {
      console.error("Cannot mount account management component: missing container element");
      return;
    }

    try {
      // Clear any existing content
      this.accountManagementContainer.nativeElement.innerHTML = '';

      // Create the Stripe embedded component (name configurable via data.stripeComponentName)
      const options = this.bundleItem ? { bundleItem: this.bundleItem } : undefined;
      this.accountManagementComponent = this.stripeConnectInstance.create(this.stripeComponentName, options);

      if (!this.accountManagementComponent) {
        console.error("Failed to create account management component - create() returned null");
        return;
      }

      // Append to container
      this.accountManagementContainer.nativeElement.appendChild(this.accountManagementComponent);
      
      // Add error event listener to intercept and customize error messages
      this.setupErrorHandling(this.accountManagementComponent, this.accountManagementContainer.nativeElement);
      
      console.log("Account management component mounted successfully", this.bundleItem ? `with bundleItem: ${this.bundleItem}` : '');
    } catch (error) {
      console.error("Error mounting account management component:", error);
    }
  }

  private createAndMountOnboardingComponent() {
    if (!this.stripeConnectInstance) {
      console.error("Cannot create onboarding component: missing Stripe Connect instance");
      return;
    }

    if (!this.accountManagementContainer?.nativeElement) {
      console.error("Cannot mount onboarding component: missing container element");
      return;
    }

    try {
      // Clear any existing content
      this.accountManagementContainer.nativeElement.innerHTML = '';
      
      // Create account-onboarding component (correct Stripe Connect component name)
      this.onboardingComponent = this.stripeConnectInstance.create('account-onboarding');
      
      if (!this.onboardingComponent) {
        console.error("Failed to create account-onboarding component - create() returned null");
        return;
      }
      
      // Append onboarding component to container
      this.accountManagementContainer.nativeElement.appendChild(this.onboardingComponent);
      
      // Add error event listener to intercept and customize error messages
      this.setupErrorHandling(this.onboardingComponent, this.accountManagementContainer.nativeElement);
      
      console.log("Account-onboarding component created and mounted successfully");
    } catch (error) {
      console.error("Error creating/mounting account-onboarding component:", error);
    }
  }

  private mountOnboardingComponent() {
    if (!this.onboardingComponent) {
      console.error("Cannot mount onboarding component: missing component");
      return;
    }

    if (!this.accountManagementContainer?.nativeElement) {
      console.error("Cannot mount onboarding component: missing container element");
      return;
    }

    try {
      // Clear any existing content
      this.accountManagementContainer.nativeElement.innerHTML = '';
      
      // Append onboarding component to container
      this.accountManagementContainer.nativeElement.appendChild(this.onboardingComponent);
      
      // Add error event listener to intercept and customize error messages
      this.setupErrorHandling(this.onboardingComponent, this.accountManagementContainer.nativeElement);
      
      console.log("Onboarding component mounted successfully");
    } catch (error) {
      console.error("Error mounting onboarding component:", error);
    }
  }

  private cleanup() {
    // Clean up MutationObserver if it exists
    if (this.accountManagementContainer?.nativeElement) {
      const observer = (this.accountManagementContainer.nativeElement as any)._stripeErrorObserver;
      if (observer) {
        observer.disconnect();
        delete (this.accountManagementContainer.nativeElement as any)._stripeErrorObserver;
      }
    }

    if (this.accountManagementComponent && this.accountManagementContainer?.nativeElement) {
      try {
        // Remove the component from DOM
        if (this.accountManagementComponent.parentNode) {
          this.accountManagementComponent.parentNode.removeChild(this.accountManagementComponent);
        }
      } catch (error) {
        console.error("Error cleaning up account management component:", error);
      }
    }

    if (this.onboardingComponent && this.accountManagementContainer?.nativeElement) {
      try {
        // Remove the onboarding component from DOM
        if (this.onboardingComponent.parentNode) {
          this.onboardingComponent.parentNode.removeChild(this.onboardingComponent);
        }
      } catch (error) {
        console.error("Error cleaning up onboarding component:", error);
      }
    }

    this.accountManagementComponent = null;
    this.onboardingComponent = null;
  }

  closeClicked() {
    this.dlg.close();
  }

  /**
   * Sets up error handling to intercept and customize Stripe Connect error messages
   * Removes the unwanted text "Puoi ancora vedere i tuoi dati in Stripe." from error messages
   */
  private setupErrorHandling(component: any, container: HTMLElement) {
    if (!component || !container) {
      return;
    }

    // Listen for loadError events from the component
    if (component.addEventListener) {
      component.addEventListener('loadError', (event: any) => {
        console.log('Stripe Connect loadError event:', event);
        // The error message might be in event.detail or event.message
        // We'll handle it via DOM mutation observer instead
      });
    }

    // Use MutationObserver to intercept and modify error messages in the DOM
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            
            // Search for error messages containing the unwanted text
            const walker = document.createTreeWalker(
              element,
              NodeFilter.SHOW_TEXT,
              null
            );  
          }
        });
      });
    });

    // Start observing the container for changes
    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true
    });

    // Store observer reference for cleanup if needed
    (container as any)._stripeErrorObserver = observer;
  }
}

